import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Drawer, DrawerContent, DrawerClose, DrawerTitle, DrawerDescription } from '@repo/ui/src/components/ui/drawer';
import { Separator } from '@repo/ui/src/components/ui/separator';
import { NAV_GROUPS, splitMobileNav, type NavItemDef } from '../../nav/navConfig';
import { useAuthStore } from '../../stores/authStore';
import { useCan } from '../../hooks/useCan';
import { usePaymentsSummary } from '../../hooks/usePayments';
import { OrgMenuItems } from '../org/OrgMenuPanel';

interface MoreSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Phone "Thêm" drawer: everything the sidebar shows that doesn't fit
 * `BottomTabBar`'s four tabs, plus the shared `OrgMenuItems` block (org header,
 * settings links, language, help, logout) that on desktop lives in the sidebar
 * footer's dropdown — same component, `variant="sheet"`, so the two surfaces
 * cannot drift.
 *
 * A vaul `Drawer`, not a Radix `Sheet`: this is a phone-only surface, so it
 * gets vaul's native swipe-down-to-dismiss (plus scrim tap) and grab handle
 * instead of a static panel with an X button.
 */
export function MoreSheet({ open, onOpenChange }: MoreSheetProps) {
  const { t } = useTranslation('common');
  const role = useAuthStore((s) => s.role);
  const { more } = useMemo(() => splitMobileNav(NAV_GROUPS, role), [role]);
  // Mounted unconditionally alongside BottomTabBar whenever isMobile — same
  // 403-on-MEMBER regression as DashboardLayout's own call, gated the same way.
  const canBill = useCan('manage:invoices');
  const { data: paymentsSummary } = usePaymentsSummary(canBill);
  const unmatchedCount = paymentsSummary?.unmatchedPaymentsCount ?? 0;

  const resolveLabel = (l: NavItemDef['label']) => t(l.key, { ns: l.ns });
  const badgeCount = (b: NavItemDef['badge']) => (b === 'unmatchedPayments' && unmatchedCount > 0 ? unmatchedCount : undefined);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      {/* Content must NOT be the scroll container: vaul decides
          scroll-vs-drag by looking for a scrollable CHILD — with overflow on
          Content itself every touch became a drag (couldn't scroll, and the
          handle scrolled away with the content). The inner div below owns the
          scrolling; the handle stays pinned above it. */}
      <DrawerContent className="max-h-[85vh] rounded-t-2xl">
        <DrawerTitle className="sr-only">{t('nav.tab.more')}</DrawerTitle>
        <DrawerDescription className="sr-only">{t('nav.tab.moreDescription')}</DrawerDescription>

        <div className="min-h-0 flex-1 overflow-y-auto p-6 pt-2 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
        {/* A role can have every nav item on the tab bar (MEMBER, since
            THI-129 + tab-fill) — then the sheet is the org block alone, and a
            nav list rendered anyway would leave a separator floating at the
            top with nothing above it. */}
        {more.length > 0 && (
        <>
        <nav aria-label={t('nav.tab.more')}>
          <ul className="space-y-1">
            {more.map((item) => {
              const count = badgeCount(item.badge);
              return (
                <li key={item.key}>
                  <DrawerClose asChild>
                    <Link
                      to={item.href}
                      className="flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-surface-800 transition-colors hover:bg-surface-100"
                    >
                      <item.icon className="size-5 shrink-0 text-surface-500" strokeWidth={1.8} />
                      <span className="flex-1 truncate">{resolveLabel(item.label)}</span>
                      {count !== undefined && (
                        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-semibold text-white">
                          {count}
                        </span>
                      )}
                    </Link>
                  </DrawerClose>
                </li>
              );
            })}
          </ul>
        </nav>

        <Separator className="my-3" />
        </>
        )}

        <div className="space-y-1 pb-1">
          <OrgMenuItems variant="sheet" onAfterAction={() => onOpenChange(false)} />
        </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
